import { useCallback, useContext, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import clsx from 'clsx';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import TaskContext from '@atom/components/workOrderPreview/taskView/TaskContext';
import WorkOrderPreviewContext from '@atom/components/workOrderPreview/WorkOrderPreviewContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { DatePicker, DateTimePicker } from '@atom/mui';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { convertDateToMillisGMTMidday } from '@atom/utilities/timeUtilities';
import { showWorkDateTime } from '@atom/utilities/workPreferenceUtilities';

import classes from './taskView.module.css';

const DEBOUNCE_TIME = 1000;

const TaskDueDate = () => {
  const { workOrderDetail, refetch } = useContext(WorkOrderPreviewContext);
  const { task } = useContext(TaskContext);
  const [dueDate, setDueDate] = useState<Date>(
    task.dueDate ? new Date(task.dueDate) : null,
  );

  const [taskUpdate] = useMutation(TASK_UPDATE);

  useEffect(() => {
    setDueDate(task.dueDate ? new Date(task.dueDate) : null);
  }, [task.dueDate]);

  const handleSubmit = async (date: Date) => {
    if (R.isEmpty(date) || R.isNil(date)) {
      return;
    }

    await taskUpdate({
      variables: {
        input: {
          id: task.id,
          workOrderId: workOrderDetail.id,
          dueDate: showWorkDateTime(workOrderDetail)
            ? date.valueOf()
            : convertDateToMillisGMTMidday(date),
        },
      },
    });

    await refetch();
  };

  const debouncedHandleSubmit = useCallback(
    debounce(handleSubmit, DEBOUNCE_TIME),
    [task.id, workOrderDetail.id],
  );

  const onChange = (date?: Date) => {
    setDueDate(date);

    if (!date) {
      return;
    } else if (!Number.isNaN(date.valueOf())) {
      debouncedHandleSubmit(date);
    }
  };

  const isDisabled =
    workOrderDetail.isClosed ||
    doesNotHaveRolePermissions(ROLE_SETS.INSPECTOR) ||
    task.isCompleted;

  return (
    <div
      className={clsx(
        classes.timeRowItem,
        showWorkDateTime(workOrderDetail) && classes.dateTimeInput,
      )}
    >
      <div className={classes.label}>Due Date</div>
      <div className={classes.timePickerContainer}>
        {showWorkDateTime(workOrderDetail) ? (
          <DateTimePicker
            value={dueDate}
            onChange={onChange}
            disabled={isDisabled}
            data-cy="taskPreviewDueDateInput"
          />
        ) : (
          <DatePicker
            value={dueDate}
            onChange={onChange}
            disabled={isDisabled}
            data-cy="taskPreviewDueDateInput"
          />
        )}
      </div>
    </div>
  );
};

export default TaskDueDate;
